import { defineStore } from 'pinia'
import { jsPDF }       from 'jspdf'
import { flashToast }  from '@/toast.js'
import logo            from '../../images/logo.jpg'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(input) {
    if (!input) return 'N/A'
    const d = new Date(input)
    if (isNaN(d.getTime())) return 'N/A'
    const n = d.getDate()
    const suffix =
        n >= 11 && n <= 13 ? 'th'
        : n % 10 === 1 ? 'st'
        : n % 10 === 2 ? 'nd'
        : n % 10 === 3 ? 'rd' : 'th'
    return `${n}${suffix} ${d.toLocaleString('en-GB', { month: 'long' })} ${d.getFullYear()}`
}

function resolveApplicantField(admission, field) {
    if (admission.type === 'online') {
        return admission.application?.applicant?.[field] ?? null
    }
    return admission[field] ?? null
}

function resolveFullName(admission) {
    const src = admission.type === 'online'
        ? admission.application?.applicant
        : admission
    if (!src) return 'N/A'
    return [src.first_name, src.other_name, src.last_name].filter(Boolean).join(' ')
}

// ─────────────────────────────────────────────────────────────────────────────

export const useAdmissionStore = defineStore('admission', {
    state: () => ({
        isGenerating: false,

        // School branding — mirrors the result store pattern
        header: {
            logo,
            schoolNameArabic:  'كلية ابن مسعود للتحفيظ القرآن والدراسات الإسلامية باوتشي',
            schoolNameEnglish: 'BN MAS\'UD COLLEGE FOR TAHFIZ AND ISLAMIC STUDIES',
            schoolAddress:     'Saleh Manga Primary School, Gwallaga Bauchi, Bauchi State',
            contact:           'bnmasudcollege@gmail.com, 08032981527, 08036928527, 08032607393',
            fonts: {
                arabic:    'AeCortoba',
                titleBold: 'Lateef-Bold',
                title:     'Lateef',
            },
            colors: {
                primary:   [0, 0, 150],
                secondary: [0, 100, 0],
                text:      [0, 0, 0],
            },
        },
    }),

    actions: {

        // ── Font loading (same as result store) ───────────────────────────
        async loadFonts(doc) {
            const { scoreSheetFonts } = await import('../config/fonts')
            if (!scoreSheetFonts?.length) return
            for (const f of scoreSheetFonts) {
                const res    = await fetch(f.url)
                const buf    = await res.arrayBuffer()
                const binary = new Uint8Array(buf).reduce((d, b) => d + String.fromCharCode(b), '')
                doc.addFileToVFS(f.fileName, btoa(binary))
                doc.addFont(f.fileName, f.fontName, f.weight)
            }
        },

        // ── Logo helper ───────────────────────────────────────────────────
        async getImageAsBase64(imgUrl) {
            return new Promise(resolve => {
                const img = new Image()
                img.crossOrigin = 'Anonymous'
                img.onload = () => {
                    const c = document.createElement('canvas')
                    c.width  = img.width
                    c.height = img.height
                    c.getContext('2d').drawImage(img, 0, 0)
                    resolve(c.toDataURL('image/jpeg'))
                }
                img.onerror = () => resolve(null)
                img.src = imgUrl
            })
        },

        // ── School header (identical pattern to result store) ─────────────
        async renderHeader(doc, y = 12) {
            const pw     = doc.internal.pageSize.getWidth()
            const margin = 14
            const h      = this.header

            const logoB64 = await this.getImageAsBase64(h.logo)
            if (logoB64) {
                doc.addImage(logoB64, 'JPEG', margin,              y, 19, 19)
                doc.addImage(logoB64, 'JPEG', pw - margin - 19,    y, 19, 19)
            }

            doc.setFont(h.fonts.arabic, 'normal')
            doc.setFontSize(15)
            doc.setTextColor(...h.colors.primary)
            doc.text(h.schoolNameArabic, pw / 2, y + 5, { align: 'center' })

            doc.setFont(h.fonts.titleBold, 'bold')
            doc.setFontSize(16)
            doc.setTextColor(...h.colors.secondary)
            doc.text(h.schoolNameEnglish, pw / 2, y + 12, { align: 'center' })

            doc.setFont(h.fonts.title, 'normal')
            doc.setFontSize(14)
            doc.setTextColor(...h.colors.text)
            doc.text(h.schoolAddress, pw / 2, y + 18, { align: 'center' })

            doc.setFontSize(11)
            doc.text(h.contact, pw / 2, y + 23, { align: 'center' })

            return y + 30
        },

        // ── Main entry point ──────────────────────────────────────────────
        /**
         * Generate and download an admission letter PDF.
         *
         * @param {Object} admission  — full admission object (with relations)
         *                             same shape as what AdmissionController@show returns
         */
        async printAdmissionLetter(admission) {
            if (this.isGenerating) return
            this.isGenerating = true

            try {
                const doc = new jsPDF('p', 'mm', 'a4')
                await this.loadFonts(doc)

                await this.renderAdmissionPage(doc, admission)

                const name     = resolveFullName(admission)
                const fileName = `${admission.admission_number} ${name} Admission Letter`
                    .toUpperCase()
                    .replace(/[^\w/]+/g, '_')

                doc.save(`${fileName}.PDF`)

            } catch (e) {
                console.error('Admission letter generation failed:', e)
                flashToast({ message: 'Failed to generate admission letter.', status: false })
            } finally {
                this.isGenerating = false
            }
        },

        // ── Page renderer ─────────────────────────────────────────────────
        async renderAdmissionPage(doc, adm) {
            const pw     = doc.internal.pageSize.getWidth()
            const ph     = doc.internal.pageSize.getHeight()
            const margin = 14
            const h      = this.header

            const fullName  = resolveFullName(adm)
            const className = adm.class_room?.name   ?? '—'
            const session   = adm.academic_session?.name ?? '—'
            const admNo     = adm.admission_number   ?? '—'
            const admDate   = formatDate(adm.admitted_at)
            const gender    = resolveApplicantField(adm, 'gender') ?? '—'
            const dob       = resolveApplicantField(adm, adm.type === 'online' ? 'dob' : 'date_of_birth') ?? '—'
            const phone     = resolveApplicantField(adm, 'phone') ?? '—'

            // ── Header ────────────────────────────────────────────────────
            let y = await this.renderHeader(doc, margin)

            // ── Title strip ───────────────────────────────────────────────
            doc.setFillColor(...h.colors.primary)
            doc.rect(margin, y, pw - margin * 2, 10, 'F')
            doc.setFont(h.fonts.titleBold, 'bold')
            doc.setFontSize(13)
            doc.setTextColor(255, 255, 255)
            doc.text('OFFER OF ADMISSION', pw / 2, y + 6.8, { align: 'center' })
            y += 14

            // ── Session + adm number row ──────────────────────────────────
            doc.setTextColor(...h.colors.text)
            doc.setFont(h.fonts.title, 'normal')
            doc.setFontSize(11)
            doc.text(`Academic Session: ${session}`, margin, y)
            doc.text(`Date: ${admDate}`, pw - margin, y, { align: 'right' })
            y += 8

            // ── Divider ───────────────────────────────────────────────────
            doc.setDrawColor(200, 200, 200)
            doc.setLineWidth(0.3)
            doc.line(margin, y, pw - margin, y)
            y += 7

            // ── Intro paragraph ───────────────────────────────────────────
            doc.setFont(h.fonts.title, 'normal')
            doc.setFontSize(12)
            doc.setTextColor(...h.colors.text)

            const intro = `This is to inform you that ${fullName} has been offered admission into ${className} for the ${session} academic session.`
            const introLines = doc.splitTextToSize(intro, pw - margin * 2)
            doc.text(introLines, margin, y)
            y += introLines.length * 6 + 5

            // ── Candidate details box ─────────────────────────────────────
            const rows = [
                ['Full Name',       fullName ],
                ['Admission No.',   admNo    ],
                ['Class',           className],
                ['Academic Session',session  ],
                ['Gender',          gender   ],
                ['Date of Birth',   dob.toString()],
                ['Phone',           phone    ],
            ]

            const boxX      = margin
            const boxW      = pw - margin * 2
            const rowH      = 8
            const labelW    = 55
            const totalBoxH = rows.length * rowH + rowH   // +rowH for header

            // Box border
            doc.setDrawColor(...h.colors.primary)
            doc.setLineWidth(0.4)
            doc.rect(boxX, y, boxW, totalBoxH, 'S')

            // Header row
            doc.setFillColor(...h.colors.primary)
            doc.rect(boxX, y, boxW, rowH, 'F')
            doc.setFont(h.fonts.titleBold, 'bold')
            doc.setFontSize(11)
            doc.setTextColor(255, 255, 255)
            doc.text('CANDIDATE DETAILS', pw / 2, y + rowH - 2, { align: 'center' })
            y += rowH

            // Data rows
            rows.forEach(([ label, value ], idx) => {
                const rowY = y + idx * rowH

                // Alternating fill
                if (idx % 2 === 0) {
                    doc.setFillColor(245, 247, 252)
                    doc.rect(boxX + 0.2, rowY + 0.1, boxW - 0.4, rowH - 0.1, 'F')
                }

                // Vertical divider
                doc.setDrawColor(220, 220, 220)
                doc.setLineWidth(0.2)
                doc.line(boxX + labelW, rowY, boxX + labelW, rowY + rowH)

                // Horizontal divider
                doc.line(boxX, rowY + rowH, boxX + boxW, rowY + rowH)

                // Label
                doc.setFont(h.fonts.titleBold, 'bold')
                doc.setFontSize(10)
                doc.setTextColor(80, 80, 80)
                doc.text(label, boxX + 3, rowY + rowH - 2.5)

                // Value
                doc.setFont(h.fonts.title, 'normal')
                doc.setFontSize(10)
                doc.setTextColor(...h.colors.text)
                doc.text(String(value), boxX + labelW + 3, rowY + rowH - 2.5)
            })

            y += rows.length * rowH + 10

            // ── Admission number callout ───────────────────────────────────
            doc.setFillColor(240, 244, 255)
            doc.setDrawColor(...h.colors.primary)
            doc.setLineWidth(0.5)
            doc.roundedRect(margin, y, pw - margin * 2, 14, 3, 3, 'FD')
            doc.setFont(h.fonts.titleBold, 'bold')
            doc.setFontSize(11)
            doc.setTextColor(...h.colors.primary)
            doc.text('Admission Number:', margin + 5, y + 9)
            doc.setFontSize(14)
            doc.text(admNo, margin + 55, y + 9)
            y += 20

            // ── Instructions ──────────────────────────────────────────────
            doc.setFont(h.fonts.titleBold, 'bold')
            doc.setFontSize(11)
            doc.setTextColor(...h.colors.text)
            doc.text('Important Instructions:', margin, y)
            y += 6

            const instructions = [
                '1. Please report to the school on or before the resumption date.',
                '2. Ensure all required fees are paid before registration.',
                '3. Come along with this letter and original copies of your credentials.',
                '4. This admission is subject to verification of credentials.',
                '5. Loss of this letter should be reported to the school immediately.',
            ]

            doc.setFont(h.fonts.title, 'normal')
            doc.setFontSize(11)
            instructions.forEach(line => {
                doc.text(line, margin + 3, y)
                y += 6
            })

            y += 8

            // ── Signatures ────────────────────────────────────────────────
            const sigY    = ph - 30
            const midX    = pw / 2
            const sigW    = 65

            // Left: Principal
            doc.setDrawColor(100, 100, 100)
            doc.setLineWidth(0.3)
            doc.line(margin, sigY, margin + sigW, sigY)
            doc.setFont(h.fonts.title, 'normal')
            doc.setFontSize(10)
            doc.setTextColor(80, 80, 80)
            doc.text("Principal's Signature", margin + sigW / 2, sigY + 5, { align: 'center' })
            doc.text(formatDate(new Date()), margin + sigW / 2, sigY + 9, { align: 'center' })

            // Right: School Stamp
            doc.line(pw - margin - sigW, sigY, pw - margin, sigY)
            doc.text('School Stamp', pw - margin - sigW / 2, sigY + 5, { align: 'center' })

            // ── Footer strip ──────────────────────────────────────────────
            doc.setFillColor(...h.colors.primary)
            doc.rect(0, ph - 10, pw, 10, 'F')
            doc.setFont(h.fonts.title, 'normal')
            doc.setFontSize(8)
            doc.setTextColor(255, 255, 255)
            doc.text(
                `${h.schoolNameEnglish}  ·  ${h.schoolAddress}`,
                pw / 2, ph - 4,
                { align: 'center' }
            )
        },
    },
})